<div class="register-user-container">
  <div class="header">
    <h5>
      <span *ngIf="currentStep === 'register'">Register New User</span>
      <span *ngIf="currentStep === 'storeSelection'">Select Store</span>
      <span *ngIf="currentStep === 'linkOptions'">Card Linking Options</span>
      <span *ngIf="currentStep === 'nfcReading'">NFC Card Reading</span>
    </h5>
    <button nbButton ghost (click)="cancel()">
      <nb-icon icon="close-outline"></nb-icon>
    </button>
  </div>

  <!-- STEP 1: Registration Form -->
  <div *ngIf="currentStep === 'register'">
    <!-- Existing user notification -->
    <div *ngIf="existingUser" class="existing-user-alert">
      <nb-alert status="warning">
        <h6><nb-icon icon="alert-circle-outline"></nb-icon> User Already Exists</h6>
        <p>This email is already registered in the system. Details of the existing user:</p>
        <div class="user-details">
          <p><strong>Name:</strong> {{existingUser.name}}</p>
          <p><strong>Email:</strong> {{existingUser.email}}</p>
          <p><strong>Phone:</strong> {{existingUser.countryCode ? (existingUser.countryCode + existingUser.phone) : existingUser.phone}}</p>
          <p *ngIf="existingUser.dateofbirth || existingUser.birthdate"><strong>Date of Birth:</strong> {{(existingUser.dateofbirth || existingUser.birthdate) | date:'mediumDate'}}</p>
        </div>
        <div class="existing-user-actions">
          <p>Would you like to:</p>
          <button nbButton status="primary" size="small" (click)="selectExistingUser()">
            <nb-icon icon="person-outline"></nb-icon>
            Select This User
          </button>
          <button nbButton status="basic" size="small" (click)="existingUser = null">
            <nb-icon icon="edit-outline"></nb-icon>
            Change Registration Info
          </button>
        </div>
      </nb-alert>
    </div>

    <!-- Registration form -->
    <form [formGroup]="registerForm" (ngSubmit)="onSubmit()">
      <!-- Name field -->
      <div class="form-group">
        <label class="label">Full Name *</label>
        <input nbInput fullWidth formControlName="fullName" placeholder="Enter full name">
        <div *ngIf="registerForm.get('fullName')?.touched && registerForm.get('fullName')?.errors?.['required']" class="error-message">
          Name is required
        </div>
      </div>

      <!-- Email field -->
      <div class="form-group">
        <label class="label">Email *</label>
        <div class="input-with-icon">
          <input nbInput fullWidth formControlName="email" placeholder="Enter email address" (blur)="onBlur('email')">
          <nb-icon *ngIf="loading" icon="loader-outline" pack="eva" [spin]="true"></nb-icon>
        </div>
        <div *ngIf="registerForm.get('email')?.touched && registerForm.get('email')?.errors?.['required']" class="error-message">
          Email is required
        </div>
        <div *ngIf="registerForm.get('email')?.touched && registerForm.get('email')?.errors?.['email']" class="error-message">
          Please enter a valid email
        </div>
      </div>
      
      <!-- Country and Phone fields -->
      <div class="form-row">
        <div class="form-group">
          <label class="label">Country Code *</label>
          <input nbInput fullWidth formControlName="countryCode" placeholder="e.g. +34" maxlength="4" (input)="onCountryCodeInput($event)">
          <div *ngIf="registerForm.get('countryCode')?.touched && registerForm.get('countryCode')?.errors?.['required']" class="error-message">
            Country code is required
          </div>
          <div *ngIf="registerForm.get('countryCode')?.touched && registerForm.get('countryCode')?.errors?.['pattern']" class="error-message">
            Country code must be in format +XX
          </div>
        </div>
        
        <div class="form-group">
          <label class="label">Phone Number *</label>
          <div class="input-with-icon">
            <input nbInput fullWidth formControlName="phone" placeholder="Enter phone number" (blur)="onBlur('phone')" (input)="onPhoneInput($event)">
            <nb-icon *ngIf="loading" icon="loader-outline" pack="eva" [spin]="true"></nb-icon>
          </div>
          <div *ngIf="registerForm.get('phone')?.touched && registerForm.get('phone')?.errors?.['required']" class="error-message">
            Phone number is required
          </div>
          <div *ngIf="registerForm.get('phone')?.touched && registerForm.get('phone')?.errors?.['pattern']" class="error-message">
            Phone number must be 8-15 digits
          </div>
        </div>
      </div>

      <!-- Date of birth and Age fields -->
      <div class="form-row">
        <div class="form-group">
          <label class="label">Date of Birth</label>
          <input nbInput fullWidth formControlName="dob" [nbDatepicker]="datepicker" placeholder="DD/MM/YYYY" (blur)="calculateAge(registerForm.get('dob')?.value)">
          <nb-datepicker #datepicker (dateChange)="calculateAge($event)" [format]="'DD/MM/yyyy'"></nb-datepicker>
        </div>
        
        <div class="form-group">
          <label class="label">Age</label>
          <input nbInput fullWidth formControlName="age" placeholder="Calculated age" [disabled]="true">
        </div>
      </div>

      <!-- CPR Number field -->
      <div class="form-group">
        <label class="label">CPR Number *</label>
        <input nbInput fullWidth formControlName="cprNumber" placeholder="Enter CPR number (10 digits)">
        <div *ngIf="registerForm.get('cprNumber')?.touched && registerForm.get('cprNumber')?.errors?.['required']" class="error-message">
          CPR number is required
        </div>
        <div *ngIf="registerForm.get('cprNumber')?.touched && registerForm.get('cprNumber')?.errors?.['pattern']" class="error-message">
          CPR number must be exactly 10 digits
        </div>
      </div>

      <!-- Action buttons -->
      <div class="form-actions">
        <button nbButton status="basic" (click)="cancel()">Cancel</button>
        <button nbButton status="success" type="submit" [disabled]="registerForm.invalid || loading">
          <nb-icon *ngIf="loading" icon="loader-outline" [spin]="true"></nb-icon>
          Register User
        </button>
      </div>
    </form>
  </div>

  <!-- STEP 1.5: Store Selection -->
  <div *ngIf="currentStep === 'storeSelection'" class="store-selection-container">
    <!-- User details summary -->
    <div class="user-summary">
      <h6>User Information</h6>
      <div class="user-details">
        <p><strong>Name:</strong> {{userData?.name}}</p>
        <p><strong>Email:</strong> {{userData?.email}}</p>
        <p><strong>Phone:</strong> {{userData?.phone}}</p>
      </div>
    </div>

    <div class="store-selection-form">
      <h6>Select Store to Record Visit</h6>
      <p class="description">
        Please select the store where the user is currently located. This will be recorded as a store visit before proceeding to card linking.
      </p>
      
      <div class="form-group">
        <label class="label">Store *</label>
        <select [(ngModel)]="visitorStoreId" class="form-control" name="storeSelect">
          <option value="" disabled selected>Select a store</option>
          <option *ngFor="let store of stores" [value]="store._id">{{ store.name || store.storeName || 'Unnamed store' }}</option>
        </select>
        <div *ngIf="!visitorStoreId" class="helper-text">
          A store must be selected to record the visit
        </div>
        <div *ngIf="stores.length === 0" class="helper-text" style="color: #db2c66;">
          No stores available. Please contact an administrator.
        </div>
      </div>
      
      <!-- Loading indicator -->
      <div *ngIf="loading" class="loading-container">
        <nb-spinner size="small"></nb-spinner>
        <span>Loading store information...</span>
      </div>
      
      <!-- Reload button -->
      <div *ngIf="!loading && stores.length === 0" style="margin-top: 15px;">
        <button nbButton outline status="primary" (click)="loadAvailableStores()">
          <nb-icon icon="refresh-outline"></nb-icon>
          Reload Stores
        </button>
        
        <!-- Test stores button (only for development) -->
        <button nbButton outline status="warning" (click)="loadTestStores()" style="margin-left: 10px;">
          <nb-icon icon="cube-outline"></nb-icon>
          Load Test Stores
        </button>
      </div>
    </div>
    
    <!-- Action buttons -->
    <div class="form-actions">
      <button nbButton status="basic" (click)="backFromStoreSelection()">
        <nb-icon icon="arrow-back-outline"></nb-icon>
        Back
      </button>
      <button nbButton status="primary" (click)="confirmStoreSelection()" [disabled]="!visitorStoreId">
        Confirm Store Selection
        <nb-icon icon="arrow-forward-outline"></nb-icon>
      </button>
    </div>
  </div>

  <!-- STEP 2: Card Linking Options -->
  <div *ngIf="currentStep === 'linkOptions'" class="card-linking-options">
    <!-- User details summary -->
    <div class="user-summary">
      <h6>User Information</h6>
      <div class="user-details">
        <p><strong>Name:</strong> {{userData?.name}}</p>
        <p><strong>Email:</strong> {{userData?.email}}</p>
        <p><strong>Phone:</strong> {{userData?.phone}}</p>
        <p *ngIf="userData?.dateofbirth || userData?.birthdate">
          <strong>Date of Birth:</strong> {{(userData?.dateofbirth || userData?.birthdate) | date:'mediumDate'}}
        </p>
        <p *ngIf="visitorStoreId && stores.length > 0">
          <strong>Selected Store:</strong> 
          {{ getStoreName(visitorStoreId) }}
        </p>
      </div>
    </div>

    <div class="linking-options">
      <h6>How would you like to link a card to this user?</h6>
      
      <!-- Option 1: Link with existing card -->
      <div class="option-card">
        <nb-card>
          <nb-card-body>
            <div class="option-content">
              <nb-icon icon="credit-card-outline" status="primary" class="option-icon"></nb-icon>
              <div class="option-details">
                <h6>Link Existing Card</h6>
                <p>Return to the Card UID Management panel to select a card</p>
              </div>
            </div>
            <button nbButton status="primary" fullWidth (click)="openCardLinkDialog()" [disabled]="loading">
              <nb-icon *ngIf="loading" icon="loader-outline" pack="eva" [spin]="true"></nb-icon>
              Return to Card Selection Panel
            </button>
          </nb-card-body>
        </nb-card>
      </div>
      
      <!-- Option 2: Read NFC directly (if supported) -->
      <div class="option-card" *ngIf="nfcSupported">
        <nb-card>
          <nb-card-body>
            <div class="option-content">
              <nb-icon icon="smartphone-outline" status="success" class="option-icon"></nb-icon>
              <div class="option-details">
                <h6>Read NFC Card</h6>
                <p>Use device's NFC reader to scan a card directly</p>
              </div>
            </div>
            <button nbButton status="success" fullWidth (click)="startNfcReading()">
              Start NFC Scan
            </button>
          </nb-card-body>
        </nb-card>
      </div>
      
      <!-- Option 2 Alternative: NFC not supported message -->
      <div class="option-card" *ngIf="!nfcSupported">
        <nb-card>
          <nb-card-body>
            <div class="option-content">
              <nb-icon icon="smartphone-outline" status="warning" class="option-icon"></nb-icon>
              <div class="option-details">
                <h6>NFC Reading Not Available</h6>
                <p>NFC reading is not supported on this device or browser</p>
              </div>
            </div>
            <button nbButton status="warning" fullWidth disabled>
              NFC Not Supported
            </button>
          </nb-card-body>
        </nb-card>
      </div>
    </div>
    
    <!-- Back button -->
    <div class="form-actions">
      <button nbButton status="basic" (click)="backToRegistration()">
        <nb-icon icon="arrow-back-outline"></nb-icon>
        Back to Registration
      </button>
      <button nbButton status="basic" (click)="cancel()">Close</button>
    </div>
  </div>

  <!-- STEP 3: NFC Reading -->
  <div *ngIf="currentStep === 'nfcReading'" class="nfc-reading-container">
    <div class="nfc-status">
      <nb-icon *ngIf="nfcReading" icon="radio-outline" status="primary" class="nfc-icon pulse"></nb-icon>
      <nb-icon *ngIf="!nfcReading && scannedCardUID" icon="checkmark-circle-2-outline" status="success" class="nfc-icon"></nb-icon>
      <nb-icon *ngIf="!nfcReading && !scannedCardUID && nfcError" icon="close-circle-outline" status="danger" class="nfc-icon"></nb-icon>
      
      <h6 *ngIf="nfcReading">
        Waiting for NFC Card...
      </h6>
      <h6 *ngIf="!nfcReading && scannedCardUID" class="success-text">
        Card Detected!
      </h6>
      <h6 *ngIf="!nfcReading && !scannedCardUID && nfcError" class="error-text">
        NFC Error
      </h6>
    </div>
    
    <div class="nfc-instructions" *ngIf="nfcReading">
      <p>Please place your NFC card near the device</p>
      <div class="nfc-animation">
        <div class="phone-outline">
          <div class="scan-area"></div>
        </div>
        <div class="card-outline"></div>
      </div>
    </div>
    
    <div class="nfc-result" *ngIf="!nfcReading && scannedCardUID">
      <p><strong>Card UID:</strong> {{scannedCardUID}}</p>
      <p>The card has been successfully linked to the user.</p>
    </div>
    
    <div class="nfc-error" *ngIf="!nfcReading && !scannedCardUID && nfcError">
      <p>{{nfcError}}</p>
      <p>Please make sure NFC is enabled on your device and try again.</p>
    </div>
    
    <div class="form-actions">
      <button nbButton status="basic" (click)="cancelNfcReading()">
        <nb-icon icon="arrow-back-outline"></nb-icon>
        Back to Options
      </button>
      <button nbButton *ngIf="nfcReading" status="danger" (click)="cancelNfcReading()">
        Cancel Scan
      </button>
      <button nbButton *ngIf="!nfcReading && !scannedCardUID" status="primary" (click)="startNfcReading()">
        Try Again
      </button>
    </div>
  </div>
</div>

<ngx-spinner bdColor="rgba(51,51,51,0.8)" size="medium" [fullScreen]="false" color="#fff" type="ball-scale-multiple">
  <p style="color: white">Processing...</p>
</ngx-spinner> 